import { MarkdownHint } from '../markdown-hint';
import '../../assets/import-module.css';
import React, { useState, useMemo, useEffect } from 'react';
import { Dialog } from '../dialogs/dialog';
import { TabView, TabPanel } from 'primereact/tabview';
import { Tree } from 'primereact/tree';
import { TreeNode } from 'primereact/treenode';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { ModuleDto } from '../../general/dtos.js';
import { Api } from '../../general/api.js';
import { useApi } from '../../providers/api-provider';
import { GeneralErrorDialog } from '../dialogs/error-dialog';

export interface ImportConfiguration {
    importMode: 'create' | 'update';
    ignoreMissingDependencies: boolean;
    leaveOriginalDependencies: boolean;
    skipDependencies: boolean;
    existingModule?: ModuleDto;
    prerequisites: {
        moduleKey: string;
        exists: boolean;
        enabled: boolean;
        targetName?: string;
    }[];
}

export interface ImportModuleDialogProps {
    folderId?: number | null;
    visible: boolean;
    onHide: (dataImported: boolean) => void;
    moduleData: ModuleDto | null;
}

const transformToTree = (data: ModuleDto | null): TreeNode[] => {
    if (!data) return [];
    return [{
        key: data.module_key,
        label: data.name ?? 'Unnamed module',
        data: { description: data.description },
        children: (data.fragments ?? []).map((frag) => {
            const children: TreeNode[] = [];
            if (frag.actions && frag.actions.length > 0) {
                children.push({
                    key: `actions-${frag.key}`,
                    label: 'Actions',
                    children: frag.actions.map(act => ({ key: act.key, label: act.label }))
                });
            }
            if (frag.formFields && frag.formFields.length > 0) {
                children.push({
                    key: `fields-${frag.key}`,
                    label: 'Form fields',
                    children: frag.formFields.map(f => ({ key: f.key, label: f.label }))
                });
            }
            return {
                key: frag.key,
                label: frag.title ?? 'Untitled fragment',
                data: { description: frag.instructions },
                children: children
            };
        })
    }];
};

export const ImportModuleDialog: React.FC<ImportModuleDialogProps> = ({ visible, onHide, moduleData, folderId }) => {
    const api: Api = useApi();
    
    const [config, setConfig] = useState<ImportConfiguration>({
        importMode: 'create',
        ignoreMissingDependencies: false,
        leaveOriginalDependencies: false,
        skipDependencies: false,
        prerequisites: []
    });

    const [expandedKeys, setExpandedKeys] = useState<any>(
        moduleData ? { [moduleData.module_key]: true } : {}
    );
    
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [error, setError] = useState<string>('');
    const [errorTitle, setErrorTitle] = useState<string>('');
    const [errorDetails, setErrorDetails] = useState<string>('');

    const defaultState: ImportConfiguration = {
        importMode: 'create',
        ignoreMissingDependencies: false,
        leaveOriginalDependencies: false,
        skipDependencies: false,
        prerequisites: []
    };

    useEffect(() => {
        if (moduleData) {
            setExpandedKeys({ [moduleData.module_key]: true });
        }
    }, [moduleData]);

    const treeNodes = useMemo(() => transformToTree(moduleData), [moduleData]);

    useEffect(() => {
        const checkPrerequisites = async () => {
            if (!moduleData?.prerequisites) return;

            try {
                const dependencyKeys: string[] = moduleData.prerequisites.map(dep => dep.module_key);
                dependencyKeys.push(moduleData.module_key);
                const foundModules: ModuleDto[] = await api.touchModules(dependencyKeys) ?? [];
                
                const mappedPrerequisites = moduleData.prerequisites.map((dep) => {
                    const found = foundModules.find((m: any) => m.key === dep.module_key);
                    return {
                        moduleKey: dep.module_key,
                        exists: !!found,
                        enabled: !found || !dep.is_optional,
                        targetName: found?.name ?? 'Missing module'
                    };
                });

                const existingModule: ModuleDto | undefined = foundModules.find((m: any) => m.key === moduleData.module_key);

                    setConfig(prev => ({
                    ...prev,
                    existingModule,
                    prerequisites: mappedPrerequisites
                }));
            } catch (err) {
                console.error("Failed to check prerequisites:", err);
            }
        };

        checkPrerequisites();
    }, [moduleData]);

    useEffect(() => {
        if (visible) {
            // Reset config to defaults whenever the dialog becomes visible
            setConfig({
                ...defaultState,
                // If prerequisites are already loaded or dependent on moduleData, 
                // you might want to keep the prerequisites list but reset the flags
                prerequisites: config.prerequisites 
            });
            setActiveIndex(0);
            setError('');
        }
    }, [visible]);

    useEffect(() => {
        if (!config.existingModule) {
            setConfig(prev => ({ ...prev, importMode: 'create' }));
        }
    }, [config.existingModule]);

    const getSummaryMessage = () => {
        let msg = config.importMode === 'create'
            ? `Importing as new module.`
            : `Updating existing module: ${config.existingModule?.name}.`;
        if (config.ignoreMissingDependencies) msg += " Missing dependencies will be ignored.";
        if (config.importMode === 'update' && config.leaveOriginalDependencies) {
            msg += " Original dependencies will be left intact.";
        }
        return msg;
    };

    const handleImport = async () => {
        const hasMissing = config.prerequisites.some(p => !p.exists);
        
        if (hasMissing && !(config.ignoreMissingDependencies || config.leaveOriginalDependencies || config.skipDependencies)) {
            setErrorTitle('Missing dependencies');
            setError('There are missing dependencies for the module. Please review them on the Prerequisites page and confirm they may be skipped during the import or check the option to leave the existing dependencies (if updating an existing module)');
            setActiveIndex(1);
            return;
        }

        const moduleToImport: ModuleDto = prepareModuleForUpdate(moduleData!, config.existingModule,
            config.skipDependencies, config.ignoreMissingDependencies, config.leaveOriginalDependencies);
        try {
            if (config.importMode === 'create') {
                moduleToImport.folder_id = folderId ?? null;
                await api.createModule(moduleToImport);
            } else {
                delete moduleToImport.folder_id;
                await api.updateModule(moduleToImport);
            }
            onHide(true);
        } catch (error: any) {
            const message: string = error.response?.data?.message ?? 'Import error';
            setError(message);
            setErrorTitle('Error importig module');
            setErrorDetails('There was an unhandled error while trying to import a module. Please try again later or contact support :)');
        }

    };

    const dependencyKeyTemplate = (rowData: any) => {
        return rowData.exists ? rowData.targetName : rowData.moduleKey;
    };

    const prepareModuleForUpdate = (
        originalModule: ModuleDto,
        existingModule: ModuleDto | undefined,
        removeDeps: boolean,
        removeMissing: boolean,
        addOriginal: boolean
    ): ModuleDto => {
        // Start with a clean deep copy
        const newModule = JSON.parse(JSON.stringify(originalModule));

        if (removeDeps) {
            newModule.prerequisites = [];
        } else if (addOriginal && existingModule) {
            newModule.prerequisites = JSON.parse(JSON.stringify(existingModule.prerequisites || []));
        } else if (removeMissing) {
            // Filter out those where exists is false
            newModule.prerequisites = newModule.prerequisites.filter((p: any) => p.exists === true);
        } 

        return newModule;
    };

    if (!visible) return null;

    return (<>
        <Dialog 
            visible={visible} 
            onHide={() => onHide(false)} 
            header="Import module" 
            style={{ width: '70vw', height: '90vh' }}
            contentStyle={{ height: 'calc(90vh - 120px)', display: 'flex', flexDirection: 'column', padding: 0 }}
            modal
        >
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <TabPanel header="Structure">
                        <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(90vh - 250px)' }}>
                            {config.existingModule && (
                                <div style={{ marginBottom: '1rem', flexShrink: 0 }}>
                                    <label style={{ display: 'block', marginBottom: '0.5rem' }}>Import mode:</label>
                                    <Dropdown 
                                        value={config.importMode} 
                                        options={[{ label: 'Import new', value: 'create' }, { label: 'Update existing', value: 'update' }]} 
                                        onChange={(e) => setConfig({ ...config, importMode: e.value })}
                                    />
                                </div>
                            )}
                            
                            <div style={{ flexGrow: 1, overflowY: 'auto' }}>
                                <Tree 
                                    key={moduleData?.module_key}
                                    value={treeNodes} 
                                    expandedKeys={expandedKeys} 
                                    onToggle={(e) => setExpandedKeys(e.value)}
                                    nodeTemplate={(node) => <MarkdownHint content={node.data?.description}>{node.label}</MarkdownHint>}
                                />
                            </div>
                        </div>
                    </TabPanel>
                    
                    <TabPanel header="Prerequisites" contentStyle={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '1rem', overflow: 'hidden' }}>
                            <DataTable value={config.prerequisites} scrollable scrollHeight="flex" className="flex-grow-1">
                                <Column body={dependencyKeyTemplate} header="Dependency key" />
                                <Column field="exists" header="Status" body={(rowData) => rowData.exists ? 'Found' : 'Missing'} />
                            </DataTable>
                            
                            <div className="field-checkbox mt-3 flex align-items-center flex-shrink-0">
                                <Checkbox 
                                    inputId="ignoreDeps" 
                                    checked={config.ignoreMissingDependencies} 
                                    disabled={config.importMode === 'update' && config.leaveOriginalDependencies}
                                    onChange={e => setConfig({...config, ignoreMissingDependencies: !!e.checked})} 
                                />
                                <label htmlFor="ignoreDeps" className="ml-2">Ignore missing dependencies</label>
                            </div>

                            {config.importMode === 'create' && (
                                <div className="field-checkbox mt-2 flex align-items-center flex-shrink-0">
                                    <Checkbox 
                                        inputId="skipDeps" 
                                        checked={config.skipDependencies ?? false} 
                                        onChange={e => {
                                            const checked = !!e.checked;
                                            setConfig({
                                                ...config, 
                                                skipDependencies: checked,
                                            });
                                        }} 
                                    />
                                    <label htmlFor="skipDeps" className="ml-2">Skip all dependencies (create module with no prerequisites)</label>
                                </div>
                            )}

                            {config.importMode === 'update' && (
                                <div className="field-checkbox mt-2 flex align-items-center flex-shrink-0">
                                    <Checkbox 
                                        inputId="leaveOriginalDeps" 
                                        checked={config.leaveOriginalDependencies ?? false} 
                                        onChange={e => {
                                            const checked = !!e.checked;
                                            setConfig({
                                                ...config, 
                                                leaveOriginalDependencies: checked,
                                                ignoreMissingDependencies: checked ? false : config.ignoreMissingDependencies
                                            });
                                        }}
                                    />
                                    <label htmlFor="leaveOriginalDeps" className="ml-2">Leave original module dependencies intact (ignore incoming prerequisites)</label>
                                </div>
                            )}
                        </div>
                    </TabPanel>
                </TabView>
            </div>

            <div className="p-dialog-footer border-top-1 surface-border" style={{ padding: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div className="text-secondary text-sm mr-3">
                        {getSummaryMessage()}
                    </div>
                    <div>
                        <Button label="Cancel" onClick={() => onHide(false)} className="p-button-text" icon="pi pi-times"/>
                        <Button label="Import" onClick={handleImport} icon="pi pi-check"/>
                    </div>
                </div>
            </div>
        </Dialog>

        <GeneralErrorDialog
            visible={error.length > 0}
            title={errorTitle}
            message={error}
            details={errorDetails}
            onClose={() => setError('')}
        />
    </>);
};
